import {
  marketDisplayPlace,
  marketDisplayText,
  marketDriverDisplayItem,
  normalizeMarketLanguage,
} from "../utils/driverMarketCyrillic.mjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Heart,
  MapPin,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import VehicleTypePicker from "./vehicle/VehicleTypePicker.jsx";
import DriverContactActions from "./DriverContactActions.jsx";
import { buildFreightContactDraft } from "../utils/freightContactDraft.js";

function bearer(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  return /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;
}

function asList(data) {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
}

const MARKET_PAGE_SIZE = 200;

// REISOR_HUB_YANDEX_MAP_V1
// Keep map work completely outside the HUB hot path: constructing/opening
// the Yandex route URL happens only after an explicit driver tap.
function hubMapEndpoint(value) {
  const s = String(value || "").trim();
  return s === "—" ? "" : s;
}

function buildHubYandexMapUrl(item) {
  const from = hubMapEndpoint(item?.from);
  const to = hubMapEndpoint(item?.to);

  if (from && to) {
    return `https://yandex.ru/maps/?mode=routes&rtext=${encodeURIComponent(from)}~${encodeURIComponent(to)}&rtt=auto`;
  }
  if (from) return `https://yandex.ru/maps/?text=${encodeURIComponent(from)}`;
  if (to) return `https://yandex.ru/maps/?text=${encodeURIComponent(to)}`;
  return "";
}

function openHubYandexMap(item) {
  const url = buildHubYandexMapUrl(item);
  if (!url) return false;

  try {
    const tg = window?.Telegram?.WebApp;
    if (tg?.openLink) {
      tg.openLink(url);
      return true;
    }
  } catch {}

  try {
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  } catch {
    return false;
  }
}

function marketLocaleForLanguage(language) {
  const lng = normalizeMarketLanguage(language);
  if (lng === "tr") return "tr-TR";
  if (lng === "uz") return "uz-UZ";
  return "ru-RU";
}

function formatDateTime(value, locale = "ru-RU") {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function freightOriginValue(item) {
  return item?.firstSeenAt || item?.lastSeenAt || null;
}

// REISOR MARKET LIFECYCLE V3:
// Age/expiry stays anchored to firstSeenAt.
// "Updated" must reflect the latest real observation.
function freightUpdatedValue(item) {
  return item?.lastSeenAt || item?.firstSeenAt || null;
}

function reisorCargoPriorityUntilMs(item) {
  const raw =
    item?.reisorCargoPriorityUntil;

  if (!raw) return 0;

  const ms =
    new Date(raw).getTime();

  return Number.isFinite(ms)
    ? ms
    : 0;
}

function isReisorCargoPriorityActive(
  item,
  nowMs = Date.now()
) {
  return Boolean(
    item?.reisorProNative &&
    reisorCargoPriorityUntilMs(item) >
      nowMs
  );
}

function freightAgeHours(item, nowMs = Date.now()) {
  const raw = freightOriginValue(item);
  if (!raw) return null;
  const ms = new Date(raw).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, (nowMs - ms) / (60 * 60 * 1000));
}

function freightAgeToneClass(item) {
  const age = freightAgeHours(item);
  if (age == null) return "reisor-freight-age--unknown";
  if (age < 3) return "reisor-freight-age--fresh";
  if (age < 7) return "reisor-freight-age--aging";
  return "reisor-freight-age--old";
}

function formatLoadingDate(value, locale = "ru-RU") {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const d = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

function localDateIso(addDays = 0) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + addDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function weightText(kg, t, locale = "ru-RU") {
  const n = Number(kg);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const tons = n / 1000;
  const value = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(tons);
  const unit = t("freightMarket.tonShort", { defaultValue: "t" });
  return `${value} ${unit}`;
}




function fallbackCode(value) {
  const s = String(value || "").trim();
  if (!s) return "—";
  return s
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

function cargoText(item, t, language) {
  const code = String(item?.cargo || "").trim().toUpperCase();
  const display = marketDisplayText(
    String(item?.cargoDisplay || "").trim(),
    language
  );

  if (code) {
    return t(`freightMarket.cargoCodes.${code}`, {
      defaultValue: display || fallbackCode(code),
    });
  }

  return display || "—";
}

function truckOne(value, t) {
  const code = String(value || "")
    .trim()
    .toUpperCase();

  if (!code) return "";

  return t(`freightMarket.truckCodes.${code}`, {
    defaultValue: fallbackCode(code),
  });
}

function truckText(types, t) {
  const list = Array.isArray(types)
    ? types
        .map((value) => truckOne(value, t))
        .filter(Boolean)
    : [];

  return list.length
    ? list.join(" · ")
    : "—";
}

function freightContactDraft(item, t, marketLanguage) {
  return buildFreightContactDraft({
    from: item?.from,
    to: item?.to,
    truck: truckText(item?.truckTypes, t),
    language: marketLanguage,
  });
}

function marketSkipKey(driverId) {
  return `reisor_driver_market_skipped_${String(driverId || "anon")}`;
}

function marketFavoriteKey(driverId) {
  return `reisor_driver_market_favorites_${String(driverId || "anon")}`;
}

function marketUiKey(driverId) {
  return `reisor_driver_market_ui_${String(driverId || "anon")}`;
}

function marketFilterKey(driverId) {
  return `reisor_driver_market_filters_${String(driverId || "anon")}`;
}

function hasStoredMarketFilters(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return ["from", "to", "dateMode", "truck"].some((key) =>
    Object.prototype.hasOwnProperty.call(value, key)
  );
}

function readObject(key, storage = "local") {
  try {
    const store = storage === "session" ? window.sessionStorage : window.localStorage;
    const raw = store.getItem(key);
    const data = raw ? JSON.parse(raw) : {};
    return data && typeof data === "object" && !Array.isArray(data) ? data : {};
  } catch {
    return {};
  }
}

function safeFavoriteItem(item, language) {
  if (!item || !item.id) return null;
  return {
    id: String(item.id),
    from: marketDisplayPlace(String(item.from || ""), language),
    to: marketDisplayPlace(String(item.to || ""), language),
    cargo: String(item.cargo || ""),
    cargoDisplay: marketDisplayText(String(item.cargoDisplay || ""), language),
    weightKg: item.weightKg == null ? null : Number(item.weightKg),
    truckTypes: Array.isArray(item.truckTypes) ? item.truckTypes.slice(0, 8) : [],
    requiredTrucks: item.requiredTrucks == null ? null : Number(item.requiredTrucks),
    rateHint: String(item.rateHint || ""),
    ratePrimary: String(item.ratePrimary || ""),
    rateSecondary: String(item.rateSecondary || ""),
    rateCurrency: String(item.rateCurrency || ""),
    loadingDate: String(item.loadingDate || ""),
    loadingReady: Boolean(item.loadingReady),
    loadingDisplay: String(item.loadingDisplay || ""),
    firstSeenAt: item.firstSeenAt || null,
    lastSeenAt: item.lastSeenAt || null,
    expiresAt: item.expiresAt || null,
    freshness: String(item.freshness || ""),
    reisorNative: Boolean(item.reisorNative),
    reisorPriorityTier: Number(item.reisorPriorityTier || 0),
    reisorPriorityUntil: item.reisorPriorityUntil || null,
  };
}

function itemExpired(item) {
  // REISOR EXPIRY V2:
  // loadingDate is a business/loading hint, not an expiry signal.
  // A recently confirmed freight must not become expired merely
  // because local midnight has passed.
  if (String(item?.freshness || "").toUpperCase() === "EXPIRED") return true;

  if (item?.expiresAt) {
    const ms = new Date(item.expiresAt).getTime();
    if (Number.isFinite(ms) && ms <= Date.now()) return true;
  }

  return false;
}

function clientMatches(item, filters, language) {
  const locale = marketLocaleForLanguage(language);
  const from = marketDisplayPlace(
    String(filters?.from || ""),
    language
  )
    .trim()
    .toLocaleLowerCase(locale);

  const to = marketDisplayPlace(
    String(filters?.to || ""),
    language
  )
    .trim()
    .toLocaleLowerCase(locale);
  const truck = String(filters?.truck || "").trim().toUpperCase();
  const wantedDate =
    filters?.dateMode === "today"
      ? localDateIso(0)
      : filters?.dateMode === "tomorrow"
        ? localDateIso(1)
        : "";

  if (from && !String(item?.from || "").toLocaleLowerCase(locale).includes(from)) return false;
  if (to && !String(item?.to || "").toLocaleLowerCase(locale).includes(to)) return false;
  if (wantedDate && String(item?.loadingDate || "") !== wantedDate) return false;
  if (truck) {
    const list = Array.isArray(item?.truckTypes) ? item.truckTypes.map((x) => String(x || "").toUpperCase()) : [];
    if (!list.some((x) => x.includes(truck))) return false;
  }
  return true;
}

function scrollHost() {
  return document.querySelector(".page-scroll");
}

function captureMarketAnchor() {
  try {
    const host = scrollHost();
    if (!host) return null;
    const hostRect = host.getBoundingClientRect();
    const cards = Array.from(document.querySelectorAll("[data-driver-market-id]"));
    const card = cards.find((el) => el.getBoundingClientRect().bottom > hostRect.top + 8);
    if (!card) return null;
    return {
      id: String(card.getAttribute("data-driver-market-id") || ""),
      offset: card.getBoundingClientRect().top - hostRect.top,
    };
  } catch {
    return null;
  }
}

function restoreMarketAnchor(anchor) {
  if (!anchor?.id) return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      try {
        const host = scrollHost();
        const el = document.querySelector(`[data-driver-market-id="${CSS.escape(anchor.id)}"]`);
        if (!host || !el) return;
        const hostRect = host.getBoundingClientRect();
        const nowOffset = el.getBoundingClientRect().top - hostRect.top;
        host.scrollTop += nowOffset - Number(anchor.offset || 0);
      } catch {}
    });
  });
}

function marketNoticeFromError(rawError, t, { detail = false } = {}) {
  const code = String(rawError?.message || rawError || "").trim();
  const upperCode = code.toUpperCase();
  const temporaryRefresh =
    upperCode.includes("RADAR_SNAPSHOT_STALE") ||
    /^HTTP[_ ]?(502|503|504)(?:\b|$)/.test(upperCode) ||
    upperCode.includes("SERVICE_UNAVAILABLE") ||
    upperCode.includes("GATEWAY_TIMEOUT");

  if (temporaryRefresh) {
    return {
      tone: "refreshing",
      title: t("freightMarket.refreshingTitle", {
        defaultValue: "Обновляем список грузов",
      }),
      message: t("freightMarket.refreshingHint", {
        defaultValue: "Подождите несколько секунд или нажмите «Обновить».",
      }),
      canRefresh: true,
    };
  }

  if (detail && code === "DRIVER_MARKET_IN_PROGRESS") {
    return {
      tone: "refreshing",
      title: t("freightMarket.checkingFreightTitle", {
        defaultValue: "Проверяем груз",
      }),
      message: t("guestMarket.marketInProgress", {
        defaultValue: "Этот груз сейчас обрабатывается. Выберите другой или попробуйте позже.",
      }),
      canRefresh: true,
    };
  }

  if (
    detail &&
    (code === "DRIVER_MARKET_NOT_AVAILABLE" ||
      code === "RADAR_FREIGHT_NOT_ACTIVE" ||
      code === "MARKET_FREIGHT_NOT_ACTIVE")
  ) {
    return {
      tone: "warning",
      title: t("freightMarket.freightUnavailableTitle", {
        defaultValue: "Груз недоступен",
      }),
      message: t("freightMarket.noLongerAvailable", {
        defaultValue: "Груз уже недоступен или снят с рынка.",
      }),
      canRefresh: true,
    };
  }

  return {
    tone: "warning",
    title: detail
      ? t("freightMarket.detailLoadErrorTitle", {
          defaultValue: "Не удалось открыть груз",
        })
      : t("freightMarket.loadErrorTitle", {
          defaultValue: "Не удалось обновить список грузов",
        }),
    message: detail
      ? t("freightMarket.detailLoadErrorHint", {
          defaultValue: "Обновите список и попробуйте снова.",
        })
      : t("freightMarket.loadErrorHint", {
          defaultValue: "Проверьте интернет и нажмите «Обновить».",
        }),
    canRefresh: true,
  };
}

// REISOR_STAGE135B_WEB_FIRST_I18N_SAFE_SELECT_V1
export default function DriverFreightMarket({
  driverToken,
  driverId,
  active = true,
  openFavoritesSignal = 0,
  openFreightId = "",
  onFreightOpenHandled,
  onOpenSubscription,
}) {
  const { t, i18n } = useTranslation();
  const marketLanguage = normalizeMarketLanguage(
    i18n.resolvedLanguage || i18n.language || "ru"
  );
  const marketLocale = marketLocaleForLanguage(marketLanguage);
  const initialUi = readObject(marketUiKey(driverId), "session");
  const initialDurableFilters = readObject(marketFilterKey(driverId));
  const initialFilterSource = hasStoredMarketFilters(initialDurableFilters)
    ? initialDurableFilters
    : initialUi?.filters || {};
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  const loadSeqRef = useRef(0);
  const nextOffsetRef = useRef(0);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [radarTotal24h, setRadarTotal24h] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [slowLoading, setSlowLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [view, setView] = useState(initialUi?.view === "favorites" ? "favorites" : "all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCompact, setFilterCompact] = useState(false);
  const [filterHintVisible, setFilterHintVisible] = useState(false);
  const [ageWindowHours, setAgeWindowHours] = useState(24);

  const [
    cargoPriorityTick,
    setCargoPriorityTick,
  ] = useState(0);
  const filterUsedRef = useRef(false);
  const [filters, setFilters] = useState(() => ({
    from: String(initialFilterSource?.from || ""),
    to: String(initialFilterSource?.to || ""),
    dateMode: ["today", "tomorrow"].includes(initialFilterSource?.dateMode) ? initialFilterSource.dateMode : "all",
    truck: String(initialFilterSource?.truck || ""),
  }));
  const [skipped, setSkipped] = useState(() => readObject(marketSkipKey(driverId)));
  const [favorites, setFavorites] = useState(() => readObject(marketFavoriteKey(driverId)));
  const [pendingNewCount, setPendingNewCount] = useState(0);
  const [pendingSnapshot, setPendingSnapshot] = useState(null);
  const restoredScrollRef = useRef(false);
  const [savedSearchState, setSavedSearchState] = useState({
    key: "",
    status: "idle",
    message: "",
  });
  // REISOR_MATCH_V1_SAVED_FILTERS_MANAGER
  const [savedFiltersOpen, setSavedFiltersOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [savedFiltersError, setSavedFiltersError] = useState("");
  const [savedFilterBusyId, setSavedFilterBusyId] = useState("");

// REISOR_DRIVER_QUICK_ACCESS_FAVORITES_V1
  useEffect(() => {
    if (!active || !openFavoritesSignal) return;
    setDetail(null);
    setFiltersOpen(false);
    setSavedFiltersOpen(false);
    setView("favorites");
  }, [active, openFavoritesSignal]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (!loading || items.length) {
      setSlowLoading(false);
      return undefined;
    }

    setSlowLoading(false);
    const timer = window.setTimeout(() => {
      setSlowLoading(true);
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [loading, items.length]);

  useEffect(() => {
    const ui = readObject(marketUiKey(driverId), "session");
    const durableFilters = readObject(marketFilterKey(driverId));
    const filterSource = hasStoredMarketFilters(durableFilters)
      ? durableFilters
      : ui?.filters || {};

    nextOffsetRef.current = 0;
    setSkipped(readObject(marketSkipKey(driverId)));
    setFavorites(readObject(marketFavoriteKey(driverId)));
    setView(ui?.view === "favorites" ? "favorites" : "all");
    setFilters({
      from: String(filterSource?.from || ""),
      to: String(filterSource?.to || ""),
      dateMode: ["today", "tomorrow"].includes(filterSource?.dateMode) ? filterSource.dateMode : "all",
      truck: String(filterSource?.truck || ""),
    });
    filterUsedRef.current = false;
    setFilterHintVisible(false);
    setFilterCompact(false);
    setAgeWindowHours(24);
    setSavedSearchState({ key: "", status: "idle", message: "" });
    setSavedFiltersOpen(false);
    setSavedFilters([]);
    setSavedFiltersLoading(false);
    setSavedFiltersError("");
    setSavedFilterBusyId("");
    setNotice(null);
    setLoadingMore(false);
    setSlowLoading(false);
    restoredScrollRef.current = false;
  }, [driverId]);

  useEffect(() => {
    try {
      localStorage.setItem(marketSkipKey(driverId), JSON.stringify(skipped || {}));
    } catch {}
  }, [driverId, skipped]);

  useEffect(() => {
    try {
      localStorage.setItem(marketFavoriteKey(driverId), JSON.stringify(favorites || {}));
    } catch {}
  }, [driverId, favorites]);

  // REISOR_MATCH_V1_DURABLE_ACTIVE_FILTERS
  // Search criteria survive Android WebView recreation.
  // Scroll position and temporary UI state remain session-scoped.
  useEffect(() => {
    try {
      localStorage.setItem(
        marketFilterKey(driverId),
        JSON.stringify(filters || {})
      );
    } catch {}
  }, [driverId, filters]);

  // REISOR_AI_V03B_DRIVER_UX — apply AI criteria to the existing market, never a parallel market.
  useEffect(() => {
    const onApplyAiFilters = (event) => {
      const incoming = event?.detail?.filters || {};
      const next = {
        from: String(incoming?.from || ""),
        to: String(incoming?.to || ""),
        dateMode: ["today", "tomorrow"].includes(incoming?.dateMode) ? incoming.dateMode : "all",
        truck: String(incoming?.truck || "").toUpperCase(),
      };
      markFilterUsed();
      setView("all");
      setFiltersOpen(false);
      setSavedFiltersOpen(false);
      setFilterCompact(false);
      setFilters(next);
    };

    window.addEventListener("reisor_driver_apply_market_filters", onApplyAiFilters);
    return () => window.removeEventListener("reisor_driver_apply_market_filters", onApplyAiFilters);
  }, [driverId]);

  useEffect(() => {
    try {
      const prev = readObject(marketUiKey(driverId), "session");
      sessionStorage.setItem(marketUiKey(driverId), JSON.stringify({ ...prev, view, filters }));
    } catch {}
  }, [driverId, view, filters]);

  useEffect(() => {
    if (!active) return undefined;
    const host = scrollHost();
    if (!host) return undefined;
    const previousOverscroll = host.style.overscrollBehaviorY;
    host.style.overscrollBehaviorY = "none";
    setFilterCompact(host.scrollTop > 96);
    let timer = 0;
    const save = () => {
      const shouldCompact = host.scrollTop > 96;

      // REISOR_MARKET_SCROLL_COMPACT_NO_AUTOCLOSE_V2
      // Scroll controls only the CLOSED panel size.
      // A filter explicitly opened by the driver stays open.
      setFilterCompact(shouldCompact);

      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        try {
          const prev = readObject(marketUiKey(driverId), "session");
          sessionStorage.setItem(marketUiKey(driverId), JSON.stringify({ ...prev, view, filters, scrollTop: host.scrollTop }));
        } catch {}
      }, 120);
    };
    host.addEventListener("scroll", save, { passive: true });
    return () => {
      window.clearTimeout(timer);
      host.removeEventListener("scroll", save);
      host.style.overscrollBehaviorY = previousOverscroll;
    };
  }, [active, driverId, view, filters]);

  useEffect(() => {
    if (!active || restoredScrollRef.current || !items.length) return;
    const ui = readObject(marketUiKey(driverId), "session");
    const top = Number(ui?.scrollTop || 0);
    restoredScrollRef.current = true;
    if (!Number.isFinite(top) || top <= 0) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const host = scrollHost();
        if (host) host.scrollTop = Math.min(top, Math.max(0, host.scrollHeight - host.clientHeight));
      });
    });
  }, [active, driverId, items.length]);

  const favoriteCount = Object.keys(favorites || {}).length;
  const wantedLoadingDate =
    filters.dateMode === "today" ? localDateIso(0) : filters.dateMode === "tomorrow" ? localDateIso(1) : "";

  // REISOR_MATCH_V1_SAVE_SEARCH_CTA
  // Persistent watch V1 deliberately saves route + body only.
  // The temporary Today/Tomorrow browse chip must not create a stale fixed-date alert.
  const savedSearchFrom = String(filters.from || "").trim();
  const savedSearchTo = String(filters.to || "").trim();
  const savedSearchTruck = String(filters.truck || "").trim().toUpperCase();
  const savedSearchKey = [savedSearchFrom.toLowerCase(), savedSearchTo.toLowerCase(), savedSearchTruck].join("|");
  const canSaveCurrentSearch = Boolean(
    view === "all" &&
      bearer(driverToken) &&
      savedSearchFrom &&
      savedSearchTo &&
      savedSearchTruck
  );
  const currentSavedSearchState =
    savedSearchState.key === savedSearchKey
      ? savedSearchState
      : { key: savedSearchKey, status: "idle", message: "" };

  const hasActiveFilters = Boolean(
    String(filters.from || "").trim() ||
      String(filters.to || "").trim() ||
      filters.dateMode !== "all" ||
      String(filters.truck || "").trim() ||
      ageWindowHours !== 24
  );

  const truckFilterRequired =
    view === "all" &&
    !String(filters.truck || "").trim();

  const filterSummary = (() => {
    const from =
      String(filters.from || "").trim();

    const to =
      String(filters.to || "").trim();

    const truck =
      String(filters.truck || "").trim();

    const parts = [];

    if (from && to) {
      parts.push(`${from} → ${to}`);
    } else if (from) {
      parts.push(
        `${t("freightMarket.from", {
          defaultValue: "Откуда",
        })}: ${from}`
      );
    } else if (to) {
      parts.push(
        `${t("freightMarket.to", {
          defaultValue: "Куда",
        })}: ${to}`
      );
    }

    if (truck) {
      parts.push(
        truckOne(truck, t)
      );
    }

    if (filters.dateMode === "today") {
      parts.push(
        t("freightMarket.todayShort", {
          defaultValue: "Сегодня",
        })
      );
    }

    if (filters.dateMode === "tomorrow") {
      parts.push(
        t("freightMarket.tomorrowShort", {
          defaultValue: "Завтра",
        })
      );
    }

    parts.push(
      t("freightMarket.ageWindowShort", {
        hours: ageWindowHours,
        defaultValue: `до ${ageWindowHours} ч`,
      })
    );

    return parts.join(" · ");
  })();

  function markFilterUsed() {
    filterUsedRef.current = true;
    setFilterHintVisible(false);

    try {
      sessionStorage.setItem(
        `reisor_driver_market_filter_used_${String(driverId || "anon")}`,
        "1"
      );
    } catch {}
  }

  function submitFilterFromKeyboard(event) {
    if (
      String(event?.key || "") !== "Enter"
    ) {
      return;
    }

    event.preventDefault();

    markFilterUsed();

    try {
      event.currentTarget.blur();
    } catch {}

    setFilterCompact(false);
  }

  useEffect(() => {
    if (!active) return undefined;

    const key =
      `reisor_driver_market_filter_used_${String(driverId || "anon")}`;

    let alreadyUsed = false;

    try {
      alreadyUsed =
        sessionStorage.getItem(key) === "1";
    } catch {}

    filterUsedRef.current =
      alreadyUsed || hasActiveFilters;

    if (filterUsedRef.current) {
      setFilterHintVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (!filterUsedRef.current) {
        setFilterHintVisible(true);
      }
    }, 20000);

    return () =>
      window.clearTimeout(timer);

    // One reminder lifecycle per driver/session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, driverId]);

  useEffect(() => {
    if (
      !hasActiveFilters ||
      filterUsedRef.current
    ) {
      return;
    }

    markFilterUsed();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveFilters]);

  useEffect(() => {
    const nowMs = Date.now();

    const nextExpiry =
      items
        .map(
          reisorCargoPriorityUntilMs
        )
        .filter(
          (value) =>
            value > nowMs
        )
        .sort(
          (a, b) => a - b
        )[0];

    if (!nextExpiry) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () =>
          setCargoPriorityTick(
            (value) => value + 1
          ),
        Math.max(
          25,
          nextExpiry -
            nowMs +
            50
        )
      );

    return () =>
      window.clearTimeout(
        timer
      );
  }, [
    items,
    cargoPriorityTick,
  ]);

  const activeItems = useMemo(
    () => items.filter((x) => !itemExpired(x)),
    [items]
  );

  const currentVisible = useMemo(
    () => {
      const nowMs = Date.now();

      const rows =
        activeItems.filter(
          (x) =>
            !skipped?.[
              String(
                x?.id || ""
              )
            ]
        );

      return rows.sort(
        (a, b) => {
          const ap =
            isReisorCargoPriorityActive(
              a,
              nowMs
            )
              ? 1
              : 0;

          const bp =
            isReisorCargoPriorityActive(
              b,
              nowMs
            )
              ? 1
              : 0;

          if (ap !== bp) {
            return bp - ap;
          }

          if (ap && bp) {
            const at =
              new Date(
                a?.firstSeenAt ||
                0
              ).getTime() || 0;

            const bt =
              new Date(
                b?.firstSeenAt ||
                0
              ).getTime() || 0;

            return bt - at;
          }

          return 0;
        }
      );
    },
    [
      activeItems,
      skipped,
      cargoPriorityTick,
    ]
  );

  const favoriteVisible = useMemo(() => {
    const rows = Object.values(favorites || {})
      .map((x) => x?.item)
      .filter(Boolean)
      .filter((x) => clientMatches(x, filters, marketLanguage));
    return rows.sort((a, b) => {
      const ae = itemExpired(a) ? 1 : 0;
      const be = itemExpired(b) ? 1 : 0;
      if (ae !== be) return ae - be;
      const ad = String(a?.loadingDate || "");
      const bd = String(b?.loadingDate || "");
      if (ad && bd && ad !== bd) return ad.localeCompare(bd);
      const at = new Date(a?.firstSeenAt || a?.lastSeenAt || 0).getTime() || 0;
      const bt = new Date(b?.firstSeenAt || b?.lastSeenAt || 0).getTime() || 0;
      return bt - at;
    });
  }, [favorites, filters, marketLanguage]);

  const visible = view === "favorites" ? favoriteVisible : currentVisible;
  const skippedCount = Math.max(0, activeItems.length - currentVisible.length);
  const canLoadMore =
    view === "all" &&
    activeItems.length < filteredTotal;
  const showMoreLabel =
    marketLanguage === "uz"
      ? "Яна кўрсатиш"
      : marketLanguage === "tr"
        ? "Daha fazla göster"
        : "Показать ещё";

  function syncFavoritesFromList(list) {
    setFavorites((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      for (const item of list) {
        const id = String(item?.id || "");
        if (!id || !next[id]) continue;
        next[id] = { ...next[id], item: safeFavoriteItem(item, marketLanguage) };
        changed = true;
      }
      return changed ? next : prev;
    });
  }

  async function load({ silent = false, append = false, filtersOverride = filters, preservePosition = true } = {}) {
    if (!active) return;
    const requestSeq = ++loadSeqRef.current;
    const pageOffset = append ? nextOffsetRef.current : 0;
    if (append) setLoadingMore(true);
    else if (!silent) setLoading(true);
    const anchor = preservePosition ? captureMarketAnchor() : null;
    try {
      const headers = {};
      const auth = bearer(driverToken);
      if (auth) headers.Authorization = auth;
      const params = new URLSearchParams({
        limit: String(MARKET_PAGE_SIZE),
        offset: String(pageOffset),
      });

      const from =
        String(filtersOverride?.from || "").trim();

      const to =
        String(filtersOverride?.to || "").trim();

      const truck =
        String(filtersOverride?.truck || "").trim();

      const loadingDate =
        filtersOverride?.dateMode === "today"
          ? localDateIso(0)
          : filtersOverride?.dateMode === "tomorrow"
            ? localDateIso(1)
            : "";

      // REISOR_DRIVER_MARKET_SERVER_GEO_FILTER_V2
      // Route/date/body filtering runs against the
      // full eligible backend pool BEFORE response limit.
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (truck) params.set("truck", truck);
      if (loadingDate) {
        params.set("loadingDate", loadingDate);
      }
      params.set("maxAgeHours", String(ageWindowHours));

      const res = await fetch(`/api/driver/freight-market/items?${params.toString()}`, {
        method: "GET",
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (requestSeq !== loadSeqRef.current) return;
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP_${res.status}`);
      const list = asList(data).map(
        (item) =>
          marketDriverDisplayItem(
            item,
            marketLanguage
          )
      );

      if (append) {
        nextOffsetRef.current = pageOffset + list.length;
      } else if (!silent) {
        nextOffsetRef.current = list.length;
      } else if (nextOffsetRef.current === 0) {
        nextOffsetRef.current = list.length;
      }

      const nextAvailableTotal =
        Number(
          data?.availableTotal ??
          list.length ??
          0
        );
      const nextFilteredTotal =
        Number(
          data?.filteredTotal ??
          list.length ??
          0
        );

      setAvailableTotal(nextAvailableTotal);
      setRadarTotal24h(
        Number(
          data?.radarTotal24h ??
          0
        )
      );
      setFilteredTotal(nextFilteredTotal);
      syncFavoritesFromList(list);

      if (append) {
        setItems((previous) => {
          const activePrevious = previous.filter((x) => !itemExpired(x));
          const seen = new Set(activePrevious.map((x) => String(x?.id || "")));
          const extra = list.filter((x) => {
            const id = String(x?.id || "");
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          return [...activePrevious, ...extra];
        });
        setPendingSnapshot(null);
        setPendingNewCount(0);
      } else if (silent && itemsRef.current.length) {
        const previous = itemsRef.current.filter((x) => !itemExpired(x));
        const previousHead = previous.slice(0, MARKET_PAGE_SIZE);
        const nextMap = new Map(list.map((x) => [String(x?.id || ""), x]));
        const nextIds = new Set(nextMap.keys());
        const previousIds = new Set(previous.map((x) => String(x?.id || "")));
        const keptHead = previousHead
          .map((x) => nextMap.get(String(x?.id || "")))
          .filter(Boolean);
        const tailLimit = Math.max(0, nextFilteredTotal - list.length);
        const tail = previous
          .slice(MARKET_PAGE_SIZE)
          .filter((x) => !nextIds.has(String(x?.id || "")))
          .slice(0, tailLimit);
        const newRows = list.filter((x) => !previousIds.has(String(x?.id || "")));
        setItems([...keptHead, ...tail]);
        setPendingSnapshot([...list, ...tail]);
        setPendingNewCount(newRows.length);
      } else {
        setItems(list.filter((x) => !itemExpired(x)));
        setPendingSnapshot(null);
        setPendingNewCount(0);
      }
      setNotice(null);
      if (anchor) restoreMarketAnchor(anchor);
    } catch (e) {
      if (requestSeq !== loadSeqRef.current) return;
      if (!silent) {
        setNotice(
          marketNoticeFromError(
            e?.message || e || "DRIVER_MARKET_LOAD_FAILED",
            t
          )
        );
      }
    } finally {
      if (requestSeq === loadSeqRef.current) {
        if (append) setLoadingMore(false);
        else if (!silent) setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (!active) return undefined;
    let stopped = false;
    const first = window.setTimeout(() => {
      if (!stopped) void load({ filtersOverride: filters, preservePosition: true });
    }, 260);
    const timer = window.setInterval(() => {
      if (stopped) return;
      try {
        if (document.visibilityState !== "visible") return;
      } catch {}
      void load({ silent: true, filtersOverride: filters, preservePosition: true });
    }, 30000);
    return () => {
      stopped = true;
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, driverToken, filters.from, filters.to, filters.dateMode, filters.truck, ageWindowHours, marketLanguage]);

  function showPendingNew() {
    if (!Array.isArray(pendingSnapshot)) return;
    setItems(pendingSnapshot.filter((x) => !itemExpired(x)));
    setPendingSnapshot(null);
    setPendingNewCount(0);
    window.requestAnimationFrame(() => {
      const host = scrollHost();
      if (host) host.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  function loadMore() {
    if (loading || loadingMore || !canLoadMore) return;
    void load({
      append: true,
      filtersOverride: filters,
      preservePosition: true,
    });
  }

  async function openItem(id) {
    const fid = String(id || "").trim();
    if (!fid) return;
    setDetailLoading(true);
    try {
      const headers = {};
      const auth = bearer(driverToken);
      if (auth) headers.Authorization = auth;
      const res = await fetch(`/api/driver/freight-market/items/${encodeURIComponent(fid)}`, {
        method: "GET",
        headers,
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || `HTTP_${res.status}`);
      const displayItem =
        data?.item
          ? marketDriverDisplayItem(
              data.item,
              marketLanguage
            )
          : null;

      const contactAccess = String(data?.contactAccess || displayItem?.contactAccess || "");

      // REISOR_DRIVER_MARKET_EXPIRED_ACCESS_DIRECT_SUBSCRIPTION_V1
      // Active trial/subscription keeps the normal detail/contact flow.
      // Expired access skips the intermediate paywall card and opens Billing/Subscription directly.
      if (
        displayItem &&
        contactAccess === "subscription_required" &&
        typeof onOpenSubscription === "function"
      ) {
        setDetail(null);
        setNotice(null);
        onOpenSubscription();
        return true;
      }

      setDetail(
        displayItem
          ? {
              ...displayItem,
              contactAccess,
              trialUntil: data?.trialUntil || null,
            }
          : null
      );

      if (displayItem) {
        const item = safeFavoriteItem(
          displayItem,
          marketLanguage
        );
        setFavorites((prev) => {
          if (!item || !prev?.[fid]) return prev;
          return { ...(prev || {}), [fid]: { ...prev[fid], item } };
        });
      }
      setNotice(null);
      return Boolean(displayItem);
    } catch (e) {
      setNotice(
        marketNoticeFromError(
          e?.message || e || "DRIVER_MARKET_DETAIL_FAILED",
          t,
          { detail: true }
        )
      );
      setDetail(null);
      void load({ silent: true, preservePosition: true });
      return false;
    } finally {
      setDetailLoading(false);
    }
  }

  // REISOR_MATCH_STAGE51_EXACT_FREIGHT_OPEN_V1
  useEffect(() => {
    const fid = String(openFreightId || "").trim();
    if (!active || !fid) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      openItem(fid)
        .catch(() => false)
        .finally(() => {
          if (!cancelled) onFreightOpenHandled?.(fid);
        });
    }, 60);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // openItem intentionally uses current token/language state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, openFreightId, driverToken, marketLanguage]);

  function skipItem(id) {
    const fid = String(id || "").trim();
    if (!fid) return;
    setSkipped((prev) => ({ ...(prev || {}), [fid]: Date.now() }));
  }

  function clearSkipped() {
    setSkipped({});
  }

  function toggleFavorite(item) {
    const saved = safeFavoriteItem(item, marketLanguage);
    if (!saved) return;
    const id = String(saved.id);
    setFavorites((prev) => {
      const next = { ...(prev || {}) };
      if (next[id]) delete next[id];
      else next[id] = { savedAt: Date.now(), item: saved };
      return next;
    });
  }

  async function saveCurrentFreightSearch() {
    if (!canSaveCurrentSearch || currentSavedSearchState.status === "saving") return;

    setSavedSearchState({ key: savedSearchKey, status: "saving", message: "" });

    try {
      const headers = { "Content-Type": "application/json" };
      const auth = bearer(driverToken);
      if (auth) headers.Authorization = auth;

      const res = await fetch("/api/driver/freight-market/saved-searches", {
        method: "POST",
        headers,
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({
          from: savedSearchFrom,
          to: savedSearchTo,
          truck: savedSearchTruck,
          loadingDate: "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = String(data?.error || `HTTP_${res.status}`);
        let message = t("freightMarket.savedSearchError", {
          defaultValue: "Не удалось сохранить поиск.",
        });

        if (code === "SAVED_SEARCH_LIMIT_REACHED") {
          message = t("freightMarket.savedSearchLimit", {
            defaultValue: "Можно сохранить до 20 активных поисков.",
          });
        } else if (res.status === 401 || res.status === 403) {
          message = t("freightMarket.savedSearchEligibility", {
            defaultValue: "Сохранение поиска доступно подтверждённым водителям.",
          });
        }

        throw Object.assign(new Error(message), { code });
      }

      setSavedSearchState({
        key: savedSearchKey,
        status: "saved",
        message: "",
      });
      if (savedFiltersOpen) {
        void loadSavedFilters({ silent: true });
      }
    } catch (error) {
      setSavedSearchState({
        key: savedSearchKey,
        status: "error",
        message: String(
          error?.message ||
            t("freightMarket.savedSearchError", {
              defaultValue: "Не удалось сохранить поиск.",
            })
        ),
      });
    }
  }

  async function loadSavedFilters({ silent = false } = {}) {
    const auth = bearer(driverToken);
    if (!auth) {
      setSavedFilters([]);
      return;
    }

    if (!silent) setSavedFiltersLoading(true);
    setSavedFiltersError("");

    try {
      const res = await fetch("/api/driver/freight-market/saved-searches", {
        method: "GET",
        headers: { Authorization: auth },
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
      setSavedFilters(asList(data));
    } catch {
      setSavedFiltersError(
        t("freightMarket.savedFiltersLoadError", {
          defaultValue: "Не удалось загрузить сохранённые фильтры.",
        })
      );
    } finally {
      if (!silent) setSavedFiltersLoading(false);
    }
  }

  async function toggleSavedFilterNotifications(item) {
    const id = String(item?.id || "").trim();
    const auth = bearer(driverToken);
    if (!id || !auth || savedFilterBusyId) return;

    setSavedFilterBusyId(id);
    setSavedFiltersError("");
    try {
      const res = await fetch(`/api/driver/freight-market/saved-searches/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: {
          Authorization: auth,
          "Content-Type": "application/json",
        },
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({ notificationsEnabled: !item?.notificationsEnabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
      if (data?.item?.id) {
        setSavedFilters((prev) =>
          prev.map((row) => String(row?.id || "") === id ? data.item : row)
        );
      } else {
        await loadSavedFilters({ silent: true });
      }
    } catch {
      setSavedFiltersError(
        t("freightMarket.savedFiltersUpdateError", {
          defaultValue: "Не удалось изменить уведомления.",
        })
      );
    } finally {
      setSavedFilterBusyId("");
    }
  }

  async function deleteSavedFilter(item) {
    const id = String(item?.id || "").trim();
    const auth = bearer(driverToken);
    if (!id || !auth || savedFilterBusyId) return;

    const route = `${String(item?.from || "").trim()} → ${String(item?.to || "").trim()}`;
    const confirmed = window.confirm(
      t("freightMarket.savedFiltersDeleteConfirm", {
        route,
        defaultValue: `Удалить фильтр ${route}?`,
      })
    );
    if (!confirmed) return;

    setSavedFilterBusyId(id);
    setSavedFiltersError("");
    try {
      const res = await fetch(`/api/driver/freight-market/saved-searches/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { Authorization: auth },
        credentials: "include",
        cache: "no-store",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
      setSavedFilters((prev) => prev.filter((row) => String(row?.id || "") !== id));
    } catch {
      setSavedFiltersError(
        t("freightMarket.savedFiltersDeleteError", {
          defaultValue: "Не удалось удалить фильтр.",
        })
      );
    } finally {
      setSavedFilterBusyId("");
    }
  }

  function applySavedFilter(item) {
    const from = String(item?.from || "").trim();
    const to = String(item?.to || "").trim();
    const truck = String(item?.truck || "").trim().toUpperCase();
    if (!from || !to || !truck) return;

    markFilterUsed();
    setView("all");
    setFiltersOpen(false);
    setFilters({
      from,
      to,
      dateMode: "all",
      truck,
    });
    setSavedFiltersOpen(false);
  }

  function resetFilters() {
    markFilterUsed();

    setFilters({
      from: "",
      to: "",
      dateMode: "all",
      truck: "",
    });
    setAgeWindowHours(24);
  }

  function renderCard(item) {
    const id = String(item?.id || "");
    const favorite = Boolean(favorites?.[id]);
    const expired = itemExpired(item);
    const priorityUntilMs = new Date(item?.reisorPriorityUntil || "").getTime();
    const priorityTier = item?.reisorNative && Number.isFinite(priorityUntilMs) && priorityUntilMs > Date.now()
      ? Math.max(0, Math.min(3, Math.floor(Number(item?.reisorPriorityTier || 0)))) : 0;
    const priorityDiamonds = priorityTier > 0 ? "💎".repeat(priorityTier) : "";
    void cargoPriorityTick;
    const loadDate =
      formatLoadingDate(item?.loadingDate, marketLocale) ||
      (item?.loadingReady
        ? t("freightMarket.ready", { defaultValue: "Ready" })
        : "");
    const ratePrimary =
      String(item?.ratePrimary || item?.rateHint || "").trim() ||
      negotiableLabel;
    const rateIsNegotiable =
      ratePrimary.trim().toLocaleLowerCase(marketLocale) ===
      negotiableLabel.trim().toLocaleLowerCase(marketLocale);
    const rateSecondary = String(item?.rateSecondary || "").trim();
    return (
      <div
        key={id}
        data-driver-market-id={id}
        data-reisor-progressive-freight-card="v1"
        className={`rounded-2xl border bg-slate-900/90 p-4 shadow-lg ${expired ? "border-amber-700/50 opacity-80" : "border-slate-700/60"}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div
              className="text-[17px] font-black leading-snug text-white"
              data-reisor-native-priority-tier={priorityTier || undefined}
            >
              {priorityDiamonds ? `${priorityDiamonds} ` : ""}{String(item.from || "—")} → {String(item.to || "—")}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-1.5 rounded-xl bg-blue-950/60 px-2.5 py-1.5 text-[13px] font-bold text-blue-200">
                <CalendarDays className="h-4 w-4" />
                {t("freightMarket.loadingDate", { defaultValue: "Загрузка" })}: {loadDate || "—"}
              </div>
              <button
                type="button"
                onClick={() => openHubYandexMap(item)}
                disabled={expired || !buildHubYandexMapUrl(item)}
                data-reisor-hub-map-action="card-v1"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800 px-2.5 py-1.5 text-[13px] font-black text-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <MapPin className="h-4 w-4" />
                {marketLanguage === "ru"
                  ? "На карте"
                  : t("buttons.show_map", { defaultValue: "Map" })}
              </button>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <div className="text-[13px] font-bold uppercase tracking-wide text-slate-400">
              {t("freightMarket.rate", { defaultValue: "Цена" })}
            </div>
            <div
              className={`max-w-[165px] text-right font-black leading-tight text-slate-100 ${
                rateIsNegotiable ? "text-[17px]" : "text-[20px]"
              }`}
              data-reisor-negotiable-size={rateIsNegotiable ? "compact-v1" : undefined}
            >
              {ratePrimary || "—"}
            </div>
            <button
              type="button"
              onClick={() => toggleFavorite(item)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border ${favorite ? "border-rose-500/50 bg-rose-950/40 text-rose-300" : "border-slate-700 bg-slate-800 text-slate-300"}`}
              aria-label={t("freightMarket.favorite", { defaultValue: "В избранное" })}
            >
              <Heart className={`h-5 w-5 ${favorite ? "fill-current" : ""}`} />
            </button>
          </div>
        </div>

        {rateSecondary ? (
          <div className="mt-2 rounded-xl bg-slate-800/80 px-2.5 py-1.5 text-[13px] font-bold text-slate-300">
            {rateSecondary}
          </div>
        ) : null}

        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
          <div className="text-slate-400">{t("freightMarket.cargo", { defaultValue: "Груз" })}</div>
          <div className="text-right font-semibold text-slate-100">{cargoText(item, t, marketLanguage)}</div>
          <div className="text-slate-400">{t("freightMarket.weight", { defaultValue: "Вес" })}</div>
          <div className="text-right font-semibold text-slate-100">{weightText(item.weightKg, t, marketLocale)}</div>
          <div className="text-slate-400" data-reisor-market-bodytype-label="v3">{t("profile.bodyType", { defaultValue: "Тип кузова" })}</div>
          <div className="text-right font-semibold text-slate-100">{truckText(item.truckTypes, t)}</div>
          {Number(item.requiredTrucks || 0) > 1 ? (
            <>
              <div className="text-slate-400">{t("freightMarket.truckCount", { defaultValue: "Машин" })}</div>
              <div className="text-right font-semibold text-slate-100">{Math.floor(Number(item.requiredTrucks))}</div>
            </>
          ) : null}
        </div>

        <div className={`mt-3 reisor-freight-age ${freightAgeToneClass(item)}`} data-reisor-freight-age={Math.floor(freightAgeHours(item) ?? -1)}>
          {t("freightMarket.updated", { defaultValue: "Обновлено" })}: {formatDateTime(freightUpdatedValue(item), marketLocale)}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => openItem(id)}
            disabled={detailLoading || expired}
            className="rounded-xl bg-blue-600 px-3 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-45"
          >
            {expired
              ? t("freightMarket.expired", { defaultValue: "Истёк" })
              : t("freightMarket.open", { defaultValue: "Открыть" })}
          </button>
          {view === "favorites" ? (
            <button
              type="button"
              onClick={() => toggleFavorite(item)}
              className="rounded-xl bg-slate-800 px-3 py-3 text-sm font-semibold text-slate-200"
            >
              {t("freightMarket.removeFavorite", { defaultValue: "Убрать" })}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => skipItem(id)}
              className="rounded-xl bg-slate-800 px-3 py-3 text-sm font-semibold text-slate-200"
            >
              {t("requests.skip", { defaultValue: "Пропустить" })}
            </button>
          )}
        </div>
      </div>
    );
  }

  const negotiableLabel = t("freightMarket.negotiable", {
    defaultValue: "Договорная",
  });

  const detailRatePrimary = detail
    ? String(detail.ratePrimary || detail.rateHint || negotiableLabel)
    : "";

  const detailRateIsNegotiable = Boolean(
    detailRatePrimary &&
      detailRatePrimary.trim().toLocaleLowerCase(marketLocale) ===
        negotiableLabel.trim().toLocaleLowerCase(marketLocale)
  );

  return (
    <div className="pb-32" data-reisor-market-direct-subscription-gate="v1">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-base font-bold text-white">
            {t("freightMarket.title", { defaultValue: "REISOR HUB" })}
          </div>
          <div
            data-reisor-market-count-mode="total24h-v2"
            className="leading-tight tabular-nums text-white"
            data-reisor-market-count-size="v3"
          >
            {view === "favorites"
              ? (
                <span className="text-lg font-black sm:text-xl">
                  {`${t("freightMarket.favoritesCount", { defaultValue: "В избранном" })}: ${favoriteCount}`}
                </span>
              )
              : (loading && !items.length) || notice?.tone === "refreshing"
                ? (
                  <span className="text-lg font-black sm:text-xl">
                    {t("freightMarket.refreshingTitle", { defaultValue: "Обновляем список грузов" })}
                  </span>
                )
                : (
                  <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-bold text-slate-300">
                      {t("freightMarket.available", { defaultValue: "Доступно сейчас" })}
                    </span>

                    <span
                      className="text-[30px] font-black leading-none tracking-tight text-white sm:text-[34px]"
                      data-reisor-market-total24="v2"
                    >
                      {radarTotal24h > 0
                        ? Number(radarTotal24h).toLocaleString(marketLocale)
                        : "…"}
                    </span>

                    <span className="text-sm font-bold text-slate-300">
                      {t("freightMarket.cargoCountUnit", { defaultValue: "грузов" })}
                    </span>
                  </span>
                )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => load({ preservePosition: true })}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-white"
          aria-label={t("common.refresh", { defaultValue: "Обновить" })}
        >
          <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {pendingNewCount > 0 ? (
        <button
          type="button"
          onClick={showPendingNew}
          className="mb-3 flex w-full items-center justify-between rounded-2xl bg-blue-600 px-4 py-3 text-sm font-black text-white shadow-lg"
        >
          <span>{t("freightMarket.newAvailable", { defaultValue: "Новые грузы доступны" })} ({pendingNewCount})</span>
          <span>↑</span>
        </button>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 rounded-2xl border border-slate-700/50 bg-slate-900/70 p-1.5">
        <button
          type="button"
          onClick={() => setView("all")}
          className={`min-h-[42px] rounded-xl px-3 text-sm font-black ${view === "all" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"}`}
        >
          {t("freightMarket.all", { defaultValue: "Все грузы" })}
        </button>
        <button
          type="button"
          onClick={() => setView("favorites")}
          className={`min-h-[42px] rounded-xl px-3 text-sm font-black ${view === "favorites" ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300"}`}
        >
          ♡ {t("freightMarket.favorites", { defaultValue: "Избранное" })} · {favoriteCount}
        </button>
      </div>

      {/* REISOR_MATCH_STAGE32_COMPACT_SEARCH_SHEET_V3 */}
      <button
        type="button"
        onClick={() => {
          markFilterUsed();
          setSavedFiltersOpen(false);
          setFiltersOpen(true);
        }}
        className="sticky top-2 z-[80] mb-2 flex w-full items-center gap-3 rounded-2xl border border-slate-700/60 bg-slate-900/95 px-3 py-2.5 text-left shadow-sm backdrop-blur"
        data-reisor-driver-market-sticky-filter="v4-compact-search-launcher"
        style={
          view === "favorites" && favoriteCount === 0
            ? { display: "none" }
            : undefined
        }
      >
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
          <Search className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-black text-white">
            {t("freightMarket.findCargo", { defaultValue: "Найти груз" })}
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-semibold text-slate-400">
            {filterSummary ||
              t("freightMarket.findCargoHint", {
                defaultValue: "Маршрут, дата и кузов",
              })}
          </span>
        </span>
        <span className="inline-flex shrink-0 items-center gap-2">
          <span
            className="rounded-xl bg-blue-500/15 px-2.5 py-1.5 text-[13px] font-black text-blue-300"
            data-reisor-market-search-count="v1"
          >
            {view === "favorites"
              ? visible.length
              : hasActiveFilters
                ? filteredTotal
                : availableTotal}
          </span>
          <ChevronDown className="h-5 w-5 text-slate-400" />
        </span>
      </button>

      {filtersOpen ? (
        <>
          <button
            type="button"
            aria-label={t("common.close", { defaultValue: "Закрыть" })}
            onClick={() => {
              setFiltersOpen(false);
              setSavedFiltersOpen(false);
            }}
            className="fixed inset-0 z-[10010] bg-black/60"
            data-reisor-search-sheet-backdrop="v3"
          />

          <div
            className="fixed inset-x-0 bottom-0 z-[10020] mx-auto flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-3xl border border-b-0 border-slate-700 bg-slate-900 text-white shadow-2xl"
            data-reisor-search-sheet="v3"
          >
            <div className="mx-auto mt-2 h-1.5 w-11 rounded-full bg-slate-600/80" />

            <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-2">
              <div className="min-w-0">
                <div className="text-base font-black text-white">
                  {t("freightMarket.findCargo", { defaultValue: "Найти груз" })}
                </div>
                <div className="mt-0.5 truncate text-[13px] font-semibold text-slate-400">
                  {filterSummary ||
                    t("freightMarket.findCargoHint", {
                      defaultValue: "Маршрут, дата и кузов",
                    })}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFiltersOpen(false);
                  setSavedFiltersOpen(false);
                }}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-slate-200"
                aria-label={t("common.close", { defaultValue: "Закрыть" })}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-1.5 border-y border-slate-700/70 bg-slate-950/45 p-1.5">
              <button
                type="button"
                onClick={() => setSavedFiltersOpen(false)}
                className={`h-10 rounded-xl px-3 text-sm font-black ${
                  !savedFiltersOpen
                    ? "bg-blue-600 text-white"
                    : "bg-slate-800 text-slate-300"
                }`}
              >
                {t("freightMarket.filters", { defaultValue: "Фильтры" })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSavedFiltersOpen(true);
                  void loadSavedFilters();
                }}
                className={`h-10 rounded-xl px-3 text-sm font-black ${
                  savedFiltersOpen
                    ? "bg-blue-600 text-white"
                    : "bg-slate-800 text-slate-300"
                }`}
                disabled={!bearer(driverToken)}
              >
                {t("freightMarket.savedRoutesTitle", {
                  defaultValue: "Сохранённые",
                })}
                {savedFilters.length ? ` · ${savedFilters.length}` : ""}
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-3">
              {!savedFiltersOpen ? (
                <div className="space-y-3" data-reisor-search-sheet-filters="v3">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="relative block">
                      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
                      <input
                        value={filters.from}
                        data-reisor-filter-enter="from"
                        enterKeyHint="search"
                        onKeyDown={submitFilterFromKeyboard}
                        onChange={(e) => {
                          markFilterUsed();
                          setFilters((p) => ({ ...p, from: e.target.value }));
                        }}
                        placeholder={t("freightMarket.from", { defaultValue: "Откуда" })}
                        className="h-11 w-full rounded-xl border border-slate-700 bg-slate-950 pl-9 pr-2 text-sm text-white outline-none focus:border-blue-500"
                      />
                    </label>
                    <label className="relative block">
                      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
                      <input
                        value={filters.to}
                        data-reisor-filter-enter="to"
                        enterKeyHint="search"
                        onKeyDown={submitFilterFromKeyboard}
                        onChange={(e) => {
                          markFilterUsed();
                          setFilters((p) => ({ ...p, to: e.target.value }));
                        }}
                        placeholder={t("freightMarket.to", { defaultValue: "Куда" })}
                        className="h-11 w-full rounded-xl border border-slate-700 bg-slate-950 pl-9 pr-2 text-sm text-white outline-none focus:border-blue-500"
                      />
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        markFilterUsed();
                        setFilters((p) => ({
                          ...p,
                          dateMode: p.dateMode === "today" ? "all" : "today",
                        }));
                      }}
                      className={`rounded-xl px-3 py-2 text-[14px] font-bold ${
                        filters.dateMode === "today"
                          ? "bg-blue-600 text-white"
                          : "bg-slate-800 text-slate-300"
                      }`}
                    >
                      {t("freightMarket.today", { defaultValue: "Загрузка сегодня" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        markFilterUsed();
                        setFilters((p) => ({
                          ...p,
                          dateMode:
                            p.dateMode === "tomorrow" ? "all" : "tomorrow",
                        }));
                      }}
                      className={`rounded-xl px-3 py-2 text-[14px] font-bold ${
                        filters.dateMode === "tomorrow"
                          ? "bg-blue-600 text-white"
                          : "bg-slate-800 text-slate-300"
                      }`}
                    >
                      {t("freightMarket.tomorrow", { defaultValue: "Загрузка завтра" })}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2" data-reisor-freshness-filter="driver-v4-24-default">
                    {[6, 12].map((hours) => (
                      <button
                        key={hours}
                        type="button"
                        aria-pressed={ageWindowHours === hours}
                        onClick={() => {
                          markFilterUsed();
                          setAgeWindowHours((current) => current === hours ? 24 : hours);
                        }}
                        className={`rounded-xl px-3 py-2 text-[14px] font-bold ${
                          ageWindowHours === hours
                            ? "bg-blue-600 text-white"
                            : "bg-slate-800 text-slate-300"
                        }`}
                      >
                        {hours === 6
                          ? t("freightMarket.showFresh6Hours", { defaultValue: "Грузы за 6 ч" })
                          : t("freightMarket.showFresh12Hours", { defaultValue: "Грузы за 12 ч" })}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2" data-reisor-driver-market-truck-collapse="v3-picker">
                    <VehicleTypePicker
                      value={filters.truck}
                      onChange={(value) => {
                        markFilterUsed();
                        setFilters((p) => ({
                          ...p,
                          truck: String(value || ""),
                        }));
                        setFilterCompact(false);
                      }}
                      domain="freight"
                      allowAll={view !== "all"}
                      className="min-w-0 flex-1"
                    />
                    {hasActiveFilters ? (
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="h-11 rounded-xl bg-slate-800 px-3 text-[13px] font-bold text-slate-200"
                      >
                        {t("freightMarket.reset", { defaultValue: "Сбросить" })}
                      </button>
                    ) : null}
                  </div>

                  {truckFilterRequired ? (
                    <div className="text-[13px] font-semibold text-amber-300">
                      {t("freightMarket.truckRequiredHint", {
                        defaultValue: "Кузов обязателен для точного поиска.",
                      })}
                    </div>
                  ) : null}

                  {wantedLoadingDate ? (
                    <div className="text-[13px] text-slate-500">
                      {t("freightMarket.loadingDate", { defaultValue: "Загрузка" })}: {formatLoadingDate(wantedLoadingDate, marketLocale)}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => {
                      markFilterUsed();
                      setFilterCompact(true);
                      setFiltersOpen(false);
                    }}
                    className="h-11 w-full rounded-xl bg-blue-600 px-4 text-sm font-black text-white active:bg-blue-700"
                    data-reisor-search-sheet-show="v3"
                  >
                    {t("freightMarket.showFreights", {
                      defaultValue: "Показать грузы",
                    })} · {view === "favorites" ? visible.length : hasActiveFilters ? filteredTotal : availableTotal}
                  </button>

                  {canSaveCurrentSearch ? (
                    <div className="rounded-2xl border border-blue-500/25 bg-blue-950/35 p-3" data-reisor-saved-search-cta="v2-sheet">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-600/15 text-blue-300">
                          <Bell className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-black text-white">
                            {t("freightMarket.savedSearchTitle", {
                              defaultValue: "Следить за новыми грузами?",
                            })}
                          </div>
                          <div className="mt-0.5 truncate text-[13px] font-bold text-blue-200">
                            {`${savedSearchFrom} → ${savedSearchTo} · ${truckOne(savedSearchTruck, t)}`}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={saveCurrentFreightSearch}
                        disabled={
                          currentSavedSearchState.status === "saving" ||
                          currentSavedSearchState.status === "saved"
                        }
                        className={`mt-3 h-10 w-full rounded-xl px-4 text-sm font-black transition ${
                          currentSavedSearchState.status === "saved"
                            ? "bg-emerald-600/20 text-emerald-200"
                            : "bg-blue-600 text-white active:bg-blue-700 disabled:opacity-70"
                        }`}
                      >
                        {currentSavedSearchState.status === "saving"
                          ? t("freightMarket.savedSearchSaving", {
                              defaultValue: "Сохраняем…",
                            })
                          : currentSavedSearchState.status === "saved"
                            ? t("freightMarket.savedSearchSaved", {
                                defaultValue: "Поиск сохранён",
                              })
                            : t("freightMarket.saveAndNotify", {
                                defaultValue: "Сохранить и уведомлять",
                              })}
                      </button>

                      {currentSavedSearchState.status === "error" && currentSavedSearchState.message ? (
                        <div className="mt-2 text-[13px] font-semibold text-red-300">
                          {currentSavedSearchState.message}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div data-reisor-saved-filters-manager="v2-sheet">
                  {savedFiltersLoading ? (
                    <div className="px-1 py-5 text-sm font-semibold text-slate-300">
                      {t("freightMarket.savedFiltersLoading", { defaultValue: "Загружаем…" })}
                    </div>
                  ) : savedFilters.length === 0 ? (
                    <div className="px-1 py-5 text-sm font-semibold text-slate-400">
                      {t("freightMarket.savedFiltersEmpty", {
                        defaultValue: "Сохранённых фильтров пока нет.",
                      })}
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-950/55">
                      <div className="divide-y divide-slate-800">
                        {savedFilters.map((item) => {
                          const id = String(item?.id || "");
                          const busy = savedFilterBusyId === id;
                          return (
                            <div key={id} className="p-3">
                              <div className="flex items-start gap-2">
                                <button
                                  type="button"
                                  onClick={() => applySavedFilter(item)}
                                  className="min-w-0 flex-1 text-left"
                                >
                                  <div className="truncate text-sm font-black text-white">
                                    {`${String(item?.from || "").trim()} → ${String(item?.to || "").trim()}`}
                                  </div>
                                  <div className="mt-1 text-[13px] font-bold text-sky-200">
                                    {truckOne(item?.truck, t)}
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteSavedFilter(item)}
                                  disabled={busy}
                                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-red-400/20 bg-red-500/10 text-red-300 disabled:opacity-50"
                                  aria-label={t("freightMarket.savedFiltersDelete", { defaultValue: "Удалить" })}
                                  title={t("freightMarket.savedFiltersDelete", { defaultValue: "Удалить" })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              <div className="mt-2 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => applySavedFilter(item)}
                                  className="h-9 flex-1 rounded-xl bg-blue-600 px-3 text-[13px] font-black text-white active:bg-blue-700"
                                >
                                  {t("freightMarket.savedFiltersApply", { defaultValue: "Применить" })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleSavedFilterNotifications(item)}
                                  disabled={busy}
                                  className={`h-9 rounded-xl px-3 text-[13px] font-black disabled:opacity-50 ${
                                    item?.notificationsEnabled
                                      ? "bg-emerald-500/15 text-emerald-200"
                                      : "bg-slate-800 text-slate-300"
                                  }`}
                                >
                                  {item?.notificationsEnabled
                                    ? t("freightMarket.savedFiltersNotificationsOn", { defaultValue: "Уведомления ON" })
                                    : t("freightMarket.savedFiltersNotificationsOff", { defaultValue: "Уведомления OFF" })}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {savedFiltersError ? (
                    <div className="mt-2 rounded-xl border border-red-400/15 px-3 py-2 text-[13px] font-semibold text-red-300">
                      {savedFiltersError}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {skippedCount > 0 && view === "all" ? (
        <button
          type="button"
          onClick={clearSkipped}
          className="mb-3 rounded-xl bg-slate-800 px-3 py-2 text-[13px] font-semibold text-slate-200"
        >
          {t("freightMarket.restoreSkipped", { defaultValue: "Вернуть пропущенные" })} · {skippedCount}
        </button>
      ) : null}

      {notice ? (
        <div
          className={`mb-3 rounded-2xl border px-4 py-3 ${
            notice.tone === "refreshing"
              ? "border-slate-700 bg-slate-900 text-white"
              : "border-amber-400/20 bg-amber-950/20 text-amber-100"
          }`}
          role="status"
          aria-live="polite"
          data-reisor-market-refresh-single="v3"
          data-reisor-market-internal-code-hidden="v6"
        >
          <div className="text-sm font-black">{notice.title}</div>
          <div className="mt-1 text-[13px] font-semibold opacity-90">{notice.message}</div>
          {notice.canRefresh ? (
            <button
              type="button"
              onClick={() => {
                setNotice(null);
                void load({ filtersOverride: filters, preservePosition: true });
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-[13px] font-black text-white"
            >
              <RefreshCw className="h-4 w-4" />
              {t("freightMarket.refresh", { defaultValue: "Обновить" })}
            </button>
          ) : null}
        </div>
      ) : null}

      {view === "favorites" &&
      visible.length === 0 ? (
        <div
          data-reisor-favorites-empty-ux="v1"
          className="mb-3 flex justify-center"
        >
          {favoriteCount === 0 ? (
            <button
              type="button"
              onClick={() => {
                setView("all");
                setFiltersOpen(false);
              }}
              className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white"
            >
              {t(
                "freightMarket.goToAllLoads",
                {
                  defaultValue:
                    "Перейти ко всем грузам",
                }
              )}
            </button>
          ) : hasActiveFilters ? (
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-black text-white"
            >
              {t(
                "freightMarket.resetFilters",
                {
                  defaultValue:
                    "Сбросить фильтры",
                }
              )}
            </button>
          ) : null}
        </div>
      ) : null}

      {loading && view === "all" && !items.length && !notice ? (
        slowLoading ? (
          <div className="mb-3 rounded-2xl border border-slate-700 bg-slate-900 px-4 py-4 text-white" role="status" aria-live="polite">
            <div className="flex items-center gap-2 text-sm font-black">
              <RefreshCw className="h-4 w-4 animate-spin" />
              {t("freightMarket.refreshingTitle", { defaultValue: "Обновляем список грузов" })}
            </div>
            <div className="mt-1 text-[13px] font-semibold opacity-90">
              {t("freightMarket.refreshingHint", { defaultValue: "Подождите несколько секунд или нажмите «Обновить»." })}
            </div>
            <button
              type="button"
              onClick={() => void load({ filtersOverride: filters, preservePosition: true })}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-[13px] font-black text-white"
            >
              <RefreshCw className="h-4 w-4" />
              {t("freightMarket.refresh", { defaultValue: "Обновить" })}
            </button>
          </div>
        ) : (
          <div className="py-8 text-center text-slate-300/70">{t("common.loading", { defaultValue: "Загрузка…" })}</div>
        )
      ) : notice && view === "all" && !items.length ? null : visible.length === 0 ? (
        <div className="py-8 text-center text-slate-300/70">
          {view === "favorites"
            ? favoriteCount > 0 && hasActiveFilters
              ? t(
                  "freightMarket.favoriteFilteredEmpty",
                  {
                    defaultValue:
                      "Под выбранные фильтры в избранном ничего не найдено.",
                  }
                )
              : t(
                  "freightMarket.favoriteEmpty",
                  {
                    defaultValue:
                      "В избранном пока ничего нет.",
                  }
                )
            : t(
                "freightMarket.empty",
                {
                  defaultValue:
                    "Под выбранные условия сейчас нет полностью оформленных свежих грузов.",
                }
              )}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">{visible.map(renderCard)}</div>
          {canLoadMore ? (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="mt-4 inline-flex h-12 w-full items-center justify-center rounded-2xl border border-slate-700 bg-slate-900 px-4 text-sm font-black text-slate-100 shadow-sm active:bg-slate-800 disabled:opacity-60"
              data-reisor-market-load-more="driver-v1"
            >
              {loadingMore
                ? t("freightMarket.loading", { defaultValue: "Загрузка…" })
                : `${showMoreLabel} · ${Math.min(MARKET_PAGE_SIZE, Math.max(0, filteredTotal - items.length))}`}
            </button>
          ) : null}
        </>
      )}


      {detail ? (
        <div className="fixed inset-0 z-[12000] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl border border-slate-700 bg-slate-950 p-4 shadow-2xl sm:rounded-3xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold uppercase tracking-wide text-slate-400">
                  {t("freightMarket.title", { defaultValue: "REISOR HUB" })}
                </div>
                <div className="mt-1 text-xl font-black text-white">
                  {String(detail.from || "—")} → {String(detail.to || "—")}
                </div>
                <button
                  type="button"
                  onClick={() => openHubYandexMap(detail)}
                  disabled={!buildHubYandexMapUrl(detail)}
                  data-reisor-hub-map-action="detail-v1"
                  className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-[13px] font-black text-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <MapPin className="h-4 w-4" />
                  {marketLanguage === "ru"
                    ? "На карте"
                    : t("buttons.show_map", { defaultValue: "Map" })}
                </button>
              </div>
              <button
                type="button"
                onClick={() => toggleFavorite(detail)}
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${favorites?.[String(detail.id)] ? "bg-slate-700 text-white" : "bg-slate-800 text-slate-300"}`}
              >
                <Heart className={`h-5 w-5 ${favorites?.[String(detail.id)] ? "fill-current" : ""}`} />
              </button>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-3 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-700/60 bg-slate-900 p-4">
                <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-wide text-slate-400">
                  <CalendarDays className="h-4 w-4" />
                  {t("freightMarket.loadingDate", { defaultValue: "Загрузка" })}
                </div>
                <div className="mt-2 text-lg font-black text-white">{formatLoadingDate(detail.loadingDate, marketLocale) || (detail.loadingReady ? t("freightMarket.ready", { defaultValue: "Ready" }) : "—")}</div>
              </div>
              <div className="rounded-2xl border border-slate-700/60 bg-slate-900 p-4 text-right">
                <div className="text-[13px] font-bold uppercase tracking-wide text-slate-400">
                  {t("freightMarket.rate", { defaultValue: "Ставка" })}
                </div>
                <div
                  className={`mt-2 font-black text-slate-100 ${
                    detailRateIsNegotiable ? "text-xl" : "text-2xl"
                  }`}
                  data-reisor-negotiable-detail-size={detailRateIsNegotiable ? "compact-v1" : undefined}
                >
                  {detailRatePrimary}
                </div>
                {detail.rateSecondary ? (
                  <div className="mt-1 text-[13px] font-bold text-slate-300">
                    {String(detail.rateSecondary)}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-700/60 bg-slate-900 p-4">
              <div className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
                <div className="text-slate-400">{t("freightMarket.cargo", { defaultValue: "Груз" })}</div>
                <div className="text-right font-semibold text-white">{cargoText(detail, t, marketLanguage)}</div>
                <div className="text-slate-400">{t("freightMarket.weight", { defaultValue: "Вес" })}</div>
                <div className="text-right font-semibold text-white">{weightText(detail.weightKg, t, marketLocale)}</div>
                <div className="text-slate-400" data-reisor-market-detail-bodytype-label="v3">{t("profile.bodyType", { defaultValue: "Тип кузова" })}</div>
                <div className="text-right font-semibold text-white">{truckText(detail.truckTypes, t)}</div>
                {Number(detail.requiredTrucks || 0) > 1 ? (
                  <>
                    <div className="text-slate-400">{t("freightMarket.truckCount", { defaultValue: "Машин" })}</div>
                    <div className="text-right font-semibold text-white">{Math.floor(Number(detail.requiredTrucks))}</div>
                  </>
                ) : null}
                <div className="text-slate-400">{t("freightMarket.updated", { defaultValue: "Обновлено" })}</div>
                <div className={`text-right reisor-freight-age ${freightAgeToneClass(detail)}`}>{formatDateTime(freightUpdatedValue(detail), marketLocale)}</div>
              </div>
            </div>

            <div className="mt-3 rounded-2xl border border-slate-700/60 bg-slate-900 p-4">
              <div className="text-[13px] font-bold uppercase tracking-wide text-slate-400">
                {t("freightMarket.contact", { defaultValue: "Контакт" })}
              </div>
              {detail.contactPhone || detail.telegramUsername ? (
                <>
                  {detail.contactPhone ? (
                    <div className="mt-2 break-all text-lg font-black text-white">
                      {String(detail.contactPhone)}
                    </div>
                  ) : null}
                  <DriverContactActions
                    phone={detail.contactPhone}
                    telegramSenderId={detail.telegramSenderId}
                    telegramUsername={detail.telegramUsername}
                    allowPhoneTelegram={Boolean(detail.contactPhone)}
                    draftText={freightContactDraft(detail, t, marketLanguage)}
                    className={detail.contactPhone ? "mt-3" : "mt-2"}
                    variant="hub"
                  />
                </>
              ) : String(detail.contactAccess || "") === "subscription_required" ? (
                <>
                  <div className="mt-2 text-sm font-semibold leading-6 text-slate-200">
                    {t("freightMarket.contactSubscriptionRequired", {
                      defaultValue: "Пробный доступ к контактам завершён. Оформите подписку REISOR, чтобы открыть контакт.",
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDetail(null);
                      if (typeof onOpenSubscription === "function") onOpenSubscription();
                    }}
                    className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-black text-white"
                  >
                    {t("freightMarket.openSubscription", { defaultValue: "Открыть подписку" })}
                  </button>
                </>
              ) : (
                <div className="mt-2 text-sm font-semibold text-slate-300">—</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
